/**
 * useLivePromptPlayback - Live prompt audio and blendshape playback helpers
 *
 * Extracted from App.tsx as the first step toward a shared live controller that
 * can be reused by both the hosted live shell and future native SDK live mode.
 *
 * Browser-only chat live mode sync (April 2026):
 *   For long prompts the /talk MP3 fetch finishes well after the blendshape
 *   chunks have streamed over the visual websocket. The previous flow started
 *   a synthetic audio clock the moment blendshapes arrived, so the face
 *   animation was already several seconds ahead by the time the real <audio>
 *   element actually started playing. Worse, both the synthetic clock and the
 *   real audio element dispatched `avatar:audioTimeSync`, fighting for the
 *   `externalAudioTimeRef` that the renderer reads.
 *
 *   Fix: mirror the Visual IVR pattern — let the audio element be the single
 *   source of truth for timing. When blendshapes are ready in browser-only
 *   mode, BUFFER them and only dispatch `avatar:blendshapes:play` after the
 *   real <audio> element fires its first `avatar:audioTimeSync` with
 *   isPlaying=true and currentTime≈0. Don't start the synthetic clock at all
 *   (the audio element ticks every 16ms via react-audio-player's onListen).
 */

import { MutableRefObject, useCallback, useEffect, useRef } from 'react';

import type { AvatarSessionContext, AvatarSessionStatus } from '../../session';
import { getSpeechText } from '../utils/spokenText';

export interface LiveBlendshapeFrame {
  time: number;
  values: Record<string, number>;
}

export interface LivePromptPlaybackRefs {
  liveAudioSyncAnimationRef: MutableRefObject<number | null>;
  liveAudioSyncStartTimeRef: MutableRefObject<number>;
  liveAudioSyncDurationRef: MutableRefObject<number>;
  livePromptAudioRequestRef: MutableRefObject<number>;
  livePromptAudioDedupRef: MutableRefObject<{ key: string | null; timestamp: number }>;
  livePromptListeningRef: MutableRefObject<boolean>;
  livePromptPlaybackUnlockedRef: MutableRefObject<boolean>;
  liveVoicePromptStartedRef: MutableRefObject<boolean>;
  liveSpeakingRef: MutableRefObject<boolean>;
  liveThinkingRef: MutableRefObject<boolean>;
  livePlaybackCountdownRef: MutableRefObject<number | null>;
  liveSpeakingTimeoutRef: MutableRefObject<number | null>;
  liveAutoListenArmedRef: MutableRefObject<boolean>;
  liveAutoListenPendingRef: MutableRefObject<boolean>;
  isCallActiveRef: MutableRefObject<boolean>;
}

export interface UseLivePromptPlaybackOptions {
  refs: LivePromptPlaybackRefs;
  activeListen?: boolean;
  /**
   * Deterministic speak gate (7 Aug 2026, Companion v8 item 1): suppress
   * ALL spoken prompt playback until the host explicitly unlocks
   * (`unlockAudio()` / `setPromptPlaybackUnlocked(true)`), regardless of
   * the browser's autoplay verdict. Without this the widget relied on
   * autoplay policy to keep a minimized launcher quiet — which is
   * per-site/per-user heuristic (media-engagement scores), so the same
   * build greeted Keith out loud on nuamedia.com.au and silently on the
   * speech site. Same suppression seam as activeListen surfaces.
   */
  holdSpeechUntilUnlock?: boolean;
  liveVoiceCallEnabled: boolean;
  /**
   * True for the runtime host's `/visual` route — Visual IVR. In Visual IVR
   * the phone plays the audio (no browser <audio>), so we fall back to the
   * synthetic audio sync clock and start blendshapes immediately. False on
   * the builder/help/decisionmemory embed surfaces where the browser fetches
   * /runtime-api/talk and plays the MP3 locally — those need the deferred
   * dispatch path so the animation starts in lockstep with the audio.
   */
  isVisualOnly?: boolean;
  requestedLiveVoiceId?: string;
  resolvedTtsBaseUrl: string;
  resolveCurrentPromptVoiceId: () => string | undefined;
  stopAudioPlayback: () => void;
  setAudioSource: (url: string | null) => void;
  setSpeak: (speaking: boolean) => void;
  dispatchAvatarEvent: (eventType: string, payload: unknown) => void;
  setLiveStatus: (
    status: AvatarSessionStatus,
    context?: AvatarSessionContext | null,
    lastError?: string
  ) => void;
  hasDirectAvatarPlayback: () => boolean;
}

export interface UseLivePromptPlaybackReturn {
  clearLivePlaybackCountdown: () => void;
  clearLiveSpeakingTimeout: () => void;
  stopLiveSyntheticAudioSync: (finalCurrentTimeSeconds?: number) => void;
  finishLiveSpeaking: (context?: AvatarSessionContext | null) => void;
  shouldSuppressInitialActiveListenPrompt: () => boolean;
  playLivePromptAudio: (text: string) => Promise<void>;
  dispatchLiveBlendshapePlayback: (
    frames: LiveBlendshapeFrame[],
    context?: AvatarSessionContext | null
  ) => void;
  setPromptPlaybackUnlocked: (unlocked: boolean) => void;
}

function clearTimer(timerRef: MutableRefObject<number | null>): void {
  if (timerRef.current === null) {
    return;
  }

  window.clearTimeout(timerRef.current);
  timerRef.current = null;
}

/**
 * Streamed TTS support probe (6 Aug 2026, diagnostics REPORT.md §c-3).
 *
 * `GET /talk/stream` (ai-node-engine) returns raw MP3 with chunked transfer,
 * so pointing the <audio> element straight at the URL starts playback on
 * first bytes — roughly 1–2s off time-to-first-spoken-word vs. waiting for
 * the whole base64 JSON of the POST /talk path. Older ai-node-engine deploys
 * don't have the endpoint, so probe once per TTS base URL (empty text → new
 * servers answer 400, old servers 404) and fall back to POST when absent.
 * Module-level cache: one probe per base URL per page load, fired at hook
 * mount so the answer is in before the first (greeting) turn plays.
 */
const streamTtsSupportByBase = new Map<string, boolean | Promise<boolean>>();

function probeStreamTtsSupport(ttsBaseUrl: string): void {
  if (!ttsBaseUrl || streamTtsSupportByBase.has(ttsBaseUrl)) {
    return;
  }
  const probe = fetch(`${ttsBaseUrl}/talk/stream?text=`, { method: 'GET' })
    .then(response => response.status === 400)
    .catch(() => false)
    .then(supported => {
      streamTtsSupportByBase.set(ttsBaseUrl, supported);
      console.log(`[LiveMode] Streamed TTS ${supported ? 'available' : 'unavailable'} at ${ttsBaseUrl}`);
      return supported;
    });
  streamTtsSupportByBase.set(ttsBaseUrl, probe);
}

function isStreamTtsSupported(ttsBaseUrl: string): boolean {
  return streamTtsSupportByBase.get(ttsBaseUrl) === true;
}

export function useLivePromptPlayback({
  refs,
  activeListen,
  holdSpeechUntilUnlock = false,
  liveVoiceCallEnabled,
  isVisualOnly = false,
  requestedLiveVoiceId,
  resolvedTtsBaseUrl,
  resolveCurrentPromptVoiceId,
  stopAudioPlayback,
  setAudioSource,
  setSpeak,
  dispatchAvatarEvent,
  setLiveStatus,
  hasDirectAvatarPlayback
}: UseLivePromptPlaybackOptions): UseLivePromptPlaybackReturn {
  const {
    liveAudioSyncAnimationRef,
    liveAudioSyncStartTimeRef,
    liveAudioSyncDurationRef,
    livePromptAudioRequestRef,
    livePromptAudioDedupRef,
    livePromptListeningRef,
    livePromptPlaybackUnlockedRef,
    liveVoicePromptStartedRef,
    liveSpeakingRef,
    liveThinkingRef,
    livePlaybackCountdownRef,
    liveSpeakingTimeoutRef,
    liveAutoListenArmedRef,
    liveAutoListenPendingRef,
    isCallActiveRef
  } = refs;

  // Fire the streamed-TTS capability probe at mount so the answer is cached
  // before the first (greeting) turn needs to pick a playback path. One tiny
  // request per TTS base URL per page load; result cached module-level.
  useEffect(() => {
    if (!liveVoiceCallEnabled && resolvedTtsBaseUrl) {
      probeStreamTtsSupport(resolvedTtsBaseUrl);
    }
  }, [liveVoiceCallEnabled, resolvedTtsBaseUrl]);

  // Buffer for browser-only chat live mode — declared early so cleanup
  // helpers below can reference them. See dispatchLiveBlendshapePlayback
  // for the full explanation.
  const pendingFramesRef = useRef<LiveBlendshapeFrame[] | null>(null);
  const pendingContextRef = useRef<AvatarSessionContext | null>(null);
  const pendingDispatchTimeoutRef = useRef<number | null>(null);
  const pendingDispatchSeqRef = useRef<number>(0);

  const clearPendingBlendshapeBuffer = useCallback(() => {
    pendingFramesRef.current = null;
    pendingContextRef.current = null;
    if (pendingDispatchTimeoutRef.current !== null) {
      window.clearTimeout(pendingDispatchTimeoutRef.current);
      pendingDispatchTimeoutRef.current = null;
    }
  }, []);

  const clearLivePlaybackCountdown = useCallback(() => {
    clearTimer(livePlaybackCountdownRef);
  }, [livePlaybackCountdownRef]);

  const clearLiveSpeakingTimeout = useCallback(() => {
    clearTimer(liveSpeakingTimeoutRef);
    // If the speaking timeout is being cleared (cancel, hangup, new turn,
    // user pressed Listen), drop any pending audio-locked blendshape buffer
    // so a stale prompt's frames don't suddenly fire when the next bot turn
    // starts playing audio.
    clearPendingBlendshapeBuffer();
  }, [clearPendingBlendshapeBuffer, liveSpeakingTimeoutRef]);

  const stopLiveSyntheticAudioSync = useCallback((finalCurrentTimeSeconds?: number) => {
    if (liveAudioSyncAnimationRef.current !== null) {
      cancelAnimationFrame(liveAudioSyncAnimationRef.current);
      liveAudioSyncAnimationRef.current = null;
    }

    if (typeof finalCurrentTimeSeconds === 'number' && liveAudioSyncDurationRef.current > 0) {
      const durationSeconds = liveAudioSyncDurationRef.current / 1000;
      const currentTimeSeconds = Math.min(finalCurrentTimeSeconds, durationSeconds);

      dispatchAvatarEvent('avatar:audioTimeSync', {
        currentTime: currentTimeSeconds,
        duration: durationSeconds,
        isPlaying: false
      });
    }

    liveAudioSyncStartTimeRef.current = 0;
    liveAudioSyncDurationRef.current = 0;
  }, [
    dispatchAvatarEvent,
    liveAudioSyncAnimationRef,
    liveAudioSyncDurationRef,
    liveAudioSyncStartTimeRef
  ]);

  const startLiveSyntheticAudioSync = useCallback((durationMs: number) => {
    stopLiveSyntheticAudioSync();
    if (!Number.isFinite(durationMs) || durationMs <= 0) {
      return;
    }

    liveAudioSyncStartTimeRef.current = performance.now();
    liveAudioSyncDurationRef.current = durationMs;

    const tick = () => {
      const elapsedMs = performance.now() - liveAudioSyncStartTimeRef.current;
      const clampedElapsedMs = Math.min(elapsedMs, liveAudioSyncDurationRef.current);
      const currentTimeSeconds = clampedElapsedMs / 1000;
      const durationSeconds = liveAudioSyncDurationRef.current / 1000;

      dispatchAvatarEvent('avatar:audioTimeSync', {
        currentTime: currentTimeSeconds,
        duration: durationSeconds,
        isPlaying: elapsedMs < liveAudioSyncDurationRef.current
      });

      if (elapsedMs < liveAudioSyncDurationRef.current && liveSpeakingRef.current) {
        liveAudioSyncAnimationRef.current = requestAnimationFrame(tick);
        return;
      }

      stopLiveSyntheticAudioSync(currentTimeSeconds);
    };

    liveAudioSyncAnimationRef.current = requestAnimationFrame(tick);
  }, [
    dispatchAvatarEvent,
    liveAudioSyncAnimationRef,
    liveAudioSyncDurationRef,
    liveAudioSyncStartTimeRef,
    liveSpeakingRef,
    stopLiveSyntheticAudioSync
  ]);

  const finishLiveSpeaking = useCallback((context?: AvatarSessionContext | null) => {
    liveSpeakingRef.current = false;
    clearLiveSpeakingTimeout();
    stopLiveSyntheticAudioSync(
      liveAudioSyncDurationRef.current > 0 ? liveAudioSyncDurationRef.current / 1000 : undefined
    );

    if (livePromptListeningRef.current && isCallActiveRef.current && !liveThinkingRef.current) {
      if (activeListen && !liveVoiceCallEnabled && liveAutoListenArmedRef.current) {
        liveAutoListenPendingRef.current = true;
      }
      dispatchAvatarEvent('avatar:listening', { listening: true });
      setLiveStatus('listening', context);
      return;
    }

    if (!liveThinkingRef.current) {
      setLiveStatus('ready', context);
    }
  }, [
    activeListen,
    clearLiveSpeakingTimeout,
    dispatchAvatarEvent,
    isCallActiveRef,
    liveAudioSyncDurationRef,
    liveAutoListenArmedRef,
    liveAutoListenPendingRef,
    livePromptListeningRef,
    liveThinkingRef,
    liveVoiceCallEnabled,
    liveSpeakingRef,
    setLiveStatus,
    stopLiveSyntheticAudioSync
  ]);

  const shouldSuppressInitialActiveListenPrompt = useCallback(() => (
    (activeListen === true || holdSpeechUntilUnlock === true) &&
    !liveVoiceCallEnabled &&
    // Visual-only surfaces (Visual IVR + the `voice-call` transport) play
    // audio on the PHONE, not in the browser, so there is no autoplay
    // restriction and no "Listen" affordance to unlock it. Without this
    // guard a visual-only consumer that also sets `activeListen` (e.g.
    // AgentLink's phone-synced avatar) would suppress the runtime's
    // blendshape stream indefinitely — the avatar mounts and idles but the
    // mouth never moves because the unlock gesture never happens. The phone
    // IS the unlocked audio source; mirror the `audioSourceIsCamlinVoice`
    // concept used below (`liveVoiceCallEnabled || isVisualOnly`).
    !isVisualOnly &&
    !livePromptPlaybackUnlockedRef.current
  ), [activeListen, holdSpeechUntilUnlock, isVisualOnly, livePromptPlaybackUnlockedRef, liveVoiceCallEnabled]);

  const playLivePromptAudio = useCallback(async (text: string) => {
    const trimmedText = text.trim();
    if (!trimmedText || liveVoiceCallEnabled || !resolvedTtsBaseUrl) {
      return;
    }

    if (shouldSuppressInitialActiveListenPrompt()) {
      console.log('[LiveMode] Suppressing prompt audio until Listen is pressed');
      return;
    }

    const speechText = getSpeechText(trimmedText);
    const currentLivePromptVoiceId = requestedLiveVoiceId || resolveCurrentPromptVoiceId();
    const promptKey = `${currentLivePromptVoiceId || ''}:${speechText}`;
    const now = Date.now();
    if (
      livePromptAudioDedupRef.current.key === promptKey &&
      now - livePromptAudioDedupRef.current.timestamp < 1500
    ) {
      return;
    }

    livePromptAudioDedupRef.current = {
      key: promptKey,
      timestamp: now
    };

    const requestId = livePromptAudioRequestRef.current + 1;
    livePromptAudioRequestRef.current = requestId;
    stopAudioPlayback();

    // Streamed path (REPORT.md §c-3): hand the <audio> element the stream URL
    // directly — the browser starts playback on first MP3 chunks while Azure
    // is still synthesizing the tail. Visemes arrive separately over the
    // visual-channel WebSocket, so nothing here needs the JSON body.
    if (isStreamTtsSupported(resolvedTtsBaseUrl)) {
      const streamParams = new URLSearchParams({ text: speechText });
      if (currentLivePromptVoiceId) {
        streamParams.set('voice', currentLivePromptVoiceId);
      }
      setAudioSource(`${resolvedTtsBaseUrl}/talk/stream?${streamParams.toString()}`);
      setSpeak(true);
      return;
    }

    try {
      const response = await fetch(`${resolvedTtsBaseUrl}/talk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: speechText,
          voice: currentLivePromptVoiceId,
          // REPORT.md §c-2: this path never reads blendData (visemes come
          // over the WebSocket) — skip the ~0.9MB per turn. Old servers
          // ignore the flag, so this is safe against any deploy skew.
          audioOnly: true
        })
      });

      if (!response.ok) {
        throw new Error(`TTS request failed with status ${response.status}`);
      }

      const payload = await response.json() as { audioData?: unknown };
      if (requestId !== livePromptAudioRequestRef.current) {
        return;
      }

      const audioData = typeof payload.audioData === 'string' ? payload.audioData.trim() : '';
      if (!audioData) {
        throw new Error('TTS response missing audioData');
      }

      setAudioSource(`data:audio/mpeg;base64,${audioData}`);
      setSpeak(true);
    } catch (error) {
      if (requestId === livePromptAudioRequestRef.current) {
        console.error('[LiveMode] Failed to fetch prompt audio:', error);
      }
    }
  }, [
    livePromptAudioDedupRef,
    livePromptAudioRequestRef,
    liveVoiceCallEnabled,
    requestedLiveVoiceId,
    resolveCurrentPromptVoiceId,
    resolvedTtsBaseUrl,
    setAudioSource,
    setSpeak,
    shouldSuppressInitialActiveListenPrompt,
    stopAudioPlayback
  ]);

  const startSpeakingTimeout = useCallback((
    clipDurationMs: number,
    context?: AvatarSessionContext | null
  ) => {
    clearLiveSpeakingTimeout();
    liveSpeakingTimeoutRef.current = window.setTimeout(() => {
      liveSpeakingTimeoutRef.current = null;
      finishLiveSpeaking(context);
    }, clipDurationMs + 800);
  }, [clearLiveSpeakingTimeout, finishLiveSpeaking, liveSpeakingTimeoutRef]);

  const flushPendingBlendshapes = useCallback((reason: string) => {
    const frames = pendingFramesRef.current;
    const context = pendingContextRef.current;
    if (!frames || frames.length === 0) {
      pendingFramesRef.current = null;
      pendingContextRef.current = null;
      if (pendingDispatchTimeoutRef.current !== null) {
        window.clearTimeout(pendingDispatchTimeoutRef.current);
        pendingDispatchTimeoutRef.current = null;
      }
      return;
    }

    pendingFramesRef.current = null;
    pendingContextRef.current = null;
    if (pendingDispatchTimeoutRef.current !== null) {
      window.clearTimeout(pendingDispatchTimeoutRef.current);
      pendingDispatchTimeoutRef.current = null;
    }

    const clipDurationMs = frames.reduce((maxDuration, frame) => (
      Math.max(maxDuration, Number(frame.time ?? 0))
    ), 0) * 1000;

    console.log('[LiveMode] Flushing buffered blendshapes (audio-locked)', {
      frameCount: frames.length,
      reason,
      clipDurationMs
    });

    dispatchAvatarEvent('avatar:blendshapes:play', { frames });
    startSpeakingTimeout(clipDurationMs, context);
  }, [dispatchAvatarEvent, startSpeakingTimeout]);

  // Listen for the real <audio> element's first playback tick. The
  // AvatarAudioLayer dispatches `avatar:audioTimeSync` from onPlay (and from
  // react-audio-player's onListen at 16ms intervals after that). The first
  // event with isPlaying=true and currentTime near 0 is our trigger to fire
  // the buffered blendshape playback so animation starts in lockstep with
  // the MP3.
  useEffect(() => {
    const handleAudioSync = (event: Event) => {
      const detail = (event as CustomEvent<{
        currentTime?: number;
        isPlaying?: boolean;
      }>).detail;
      if (!detail) return;
      const isPlaying = detail.isPlaying !== false;
      const currentTime = typeof detail.currentTime === 'number' ? detail.currentTime : 0;
      if (!isPlaying) return;
      if (!pendingFramesRef.current) return;
      // Only treat the FIRST tick of a fresh audio playback as the trigger.
      // The audio element starts at 0; if currentTime is already > ~0.5s the
      // event is mid-stream (e.g. seek) and shouldn't restart the animation.
      if (currentTime > 0.5) return;
      flushPendingBlendshapes('audio-onPlay');
    };

    window.addEventListener('avatar:audioTimeSync', handleAudioSync as EventListener);
    return () => {
      window.removeEventListener('avatar:audioTimeSync', handleAudioSync as EventListener);
    };
  }, [flushPendingBlendshapes]);

  const dispatchLiveBlendshapePlayback = useCallback((
    frames: LiveBlendshapeFrame[],
    context?: AvatarSessionContext | null
  ) => {
    clearLivePlaybackCountdown();
    clearLiveSpeakingTimeout();

    if (!frames || frames.length === 0) {
      return;
    }

    const clipDurationMs = frames.reduce((maxDuration, frame) => (
      Math.max(maxDuration, Number(frame.time ?? 0))
    ), 0) * 1000;

    liveSpeakingRef.current = true;
    liveVoicePromptStartedRef.current = true;
    liveThinkingRef.current = false;
    dispatchAvatarEvent('avatar:listening', { listening: false });
    dispatchAvatarEvent('avatar:thinking', { thinking: false });
    setLiveStatus('speaking', context);

    // The avatar's lip-sync timing source depends on where the audio is
    // actually being played, NOT on which transport mode the live session
    // happens to use. There are exactly two audio-source families:
    //
    //   A. CamlinVoice-driven (Visual IVR phone + browser SIP/WebRTC live)
    //      Audio plays via Asterisk:
    //        - Visual IVR  : Asterisk → PSTN/SIP → caller's phone speaker
    //        - WebRTC live : Asterisk → RTP → browser → user's speakers
    //      In BOTH cases CamlinVoice publishes `voice.audio.position` every
    //      200ms (its actual playback clock since `channel.play()` started),
    //      runtime forwards it as `visual.audio.position` to the visual
    //      websocket, and `useLiveVisualChannel.onAudioPosition` feeds it
    //      into `externalAudioTimeRef` via `avatar:audioTimeSync`. The
    //      external sync is wall-clock-accurate to within one tick (200ms)
    //      and the renderer's `getAnimationTime` extrapolates the gap.
    //      We MUST NOT also run the local synthetic clock here — it fires
    //      every ~16ms (60fps) and would dominate the 200ms external sync,
    //      dragging the animation back to "performance.now() -
    //      dispatchLiveBlendshapePlaybackTime" which is approximately the
    //      audio pipeline latency ahead of when audio actually plays.
    //      That race was the symptom Keith reported on Apr 23 2026 ("audio
    //      now synchronised, then animation lags and catches up over a few
    //      seconds") — the catch-up was the renderer flipping between the
    //      synthetic clock (ahead) and the external sync (correct) every
    //      time a fresh audio.position arrived.
    //
    //   B. Browser-only chat (no CamlinVoice in the path)
    //      Used by surfaces that fetch /runtime-api/talk MP3 and play it
    //      with the in-page <audio> element. Examples: DecisionMemory's
    //      contextual native chat path, any embed without WebRTC. Here
    //      the only timing source is the <audio> element's `timeupdate`
    //      events (16ms cadence via react-audio-player), which AvatarAudio
    //      Layer dispatches as `avatar:audioTimeSync`. To avoid the
    //      synthetic clock starting BEFORE the MP3 has finished
    //      downloading (which made long prompts visibly drift on Apr 22),
    //      we BUFFER the blendshapes here and only dispatch them when the
    //      <audio> element fires its first onPlay tick.
    //
    // The branching below picks one of these two paths; nothing else
    // matters (mode flags like liveVoiceCallEnabled vs isVisualOnly only
    // matter as predicates of which family applies).
    const audioSourceIsCamlinVoice = liveVoiceCallEnabled || isVisualOnly;
    const isBrowserOnlyChatMode = !audioSourceIsCamlinVoice;

    if (isBrowserOnlyChatMode) {
      // Replace any previously-buffered playback (e.g. user barged in with
      // a new turn before the previous prompt's audio started).
      if (pendingDispatchTimeoutRef.current !== null) {
        window.clearTimeout(pendingDispatchTimeoutRef.current);
        pendingDispatchTimeoutRef.current = null;
      }
      pendingFramesRef.current = frames;
      pendingContextRef.current = context ?? null;
      pendingDispatchSeqRef.current += 1;
      const seq = pendingDispatchSeqRef.current;

      console.log('[LiveMode] Buffered blendshapes, awaiting <audio> onPlay', {
        frameCount: frames.length,
        clipDurationMs,
        hasDirectAvatarPlayback: hasDirectAvatarPlayback()
      });

      // Safety fallback: the audio element hasn't played yet (slow /talk
      // MP3 fetch for a long reply, autoplay blocked, network failure).
      // Pre-6-Aug-2026 this flushed the blendshapes at 4s and drove them
      // with a synthetic clock — the avatar's mouth moved through the
      // whole clip in guaranteed silence, which is exactly the "talks
      // with no audio" defect Keith reported. The invariant now is: the
      // mouth NEVER moves without audible speech.
      //
      // So: HOLD the frames. The audioTimeSync listener above flushes
      // them in lockstep with the audio whenever it finally starts — a
      // slow MP3 fetch just means mouth + audio both start late,
      // together. At 4s we surface the muted chip if the element shows
      // the blocked-autoplay signature (best-effort: in shadow-DOM hosts
      // like the Companion the getElementById lookup misses, and the
      // canonical playerReady NotAllowedError path covers it instead).
      // Only after a long grace window do we give up: drop the frames and
      // settle the speaking state — never animate them silently.
      pendingDispatchTimeoutRef.current = window.setTimeout(() => {
        pendingDispatchTimeoutRef.current = null;
        if (pendingDispatchSeqRef.current !== seq) return;
        if (!pendingFramesRef.current) return;

        const audioEl = typeof document !== 'undefined'
          ? document.getElementById('remoteAudio') as HTMLAudioElement | null
          : null;
        const audioElLooksAutoplayBlocked = !!audioEl
          && !!audioEl.src
          && audioEl.paused
          && !audioEl.error
          && audioEl.currentTime === 0;

        if (audioElLooksAutoplayBlocked) {
          console.warn('[LiveMode] Audio blocked by autoplay policy; holding blendshapes and surfacing muted state');
          window.dispatchEvent(new CustomEvent('avatar:audio-access-required', {
            detail: { message: 'Audio playback is blocked for this page. Allow audio playback in the browser, then retry access or press Listen again.' }
          }));
        } else {
          console.warn('[LiveMode] Audio not playing yet; holding blendshapes until it starts (mouth must not move silently)');
        }

        // Grace window: an un-mute tap or a slow MP3 arriving flushes via
        // the audioTimeSync listener; if neither happens, give up.
        pendingDispatchTimeoutRef.current = window.setTimeout(() => {
          pendingDispatchTimeoutRef.current = null;
          if (pendingDispatchSeqRef.current !== seq) return;
          if (!pendingFramesRef.current) return;
          console.warn('[LiveMode] Audio never arrived; dropping buffered blendshapes for this turn');
          pendingFramesRef.current = null;
          pendingContextRef.current = null;
          finishLiveSpeaking(context);
        }, 60000);
      }, 4000);
      return;
    }

    // CamlinVoice-driven path (Visual IVR + WebRTC live). Hand off to the
    // external sync source — see the family explanation above.
    console.log('[LiveMode] Dispatching blendshape playback', {
      frameCount: frames.length,
      audioSource: 'CamlinVoice (' + (isVisualOnly ? 'phone' : 'WebRTC') + ')',
      timingSource: 'external (visual.audio.position)',
      hasDirectAvatarPlayback: hasDirectAvatarPlayback()
    });
    dispatchAvatarEvent('avatar:blendshapes:play', { frames });
    // Synthetic clock intentionally skipped — see the family explanation
    // above. If the external sync ever stops (e.g., visual.audio.position
    // events drop off), the renderer's `getAnimationTime` automatically
    // falls back to its own local clock anchored at `animStartTime`, which
    // we anchor to the existing audio position so there's no catch-up
    // jump (see Avatar.tsx#animateFrame init for the anchor logic).
    startSpeakingTimeout(clipDurationMs, context);
  }, [
    clearLivePlaybackCountdown,
    clearLiveSpeakingTimeout,
    dispatchAvatarEvent,
    finishLiveSpeaking,
    flushPendingBlendshapes,
    hasDirectAvatarPlayback,
    isVisualOnly,
    liveSpeakingRef,
    liveThinkingRef,
    liveVoiceCallEnabled,
    liveVoicePromptStartedRef,
    setLiveStatus,
    startSpeakingTimeout
  ]);

  const setPromptPlaybackUnlocked = useCallback((unlocked: boolean) => {
    livePromptPlaybackUnlockedRef.current = unlocked;
  }, [livePromptPlaybackUnlockedRef]);

  // Cleanup any pending blendshape buffer on unmount (e.g. user closes the
  // chat panel mid-prompt) — otherwise we'd leak the fallback timeout and
  // the listener-driven dispatch would fire into a torn-down session.
  useEffect(() => {
    return () => {
      clearPendingBlendshapeBuffer();
    };
  }, [clearPendingBlendshapeBuffer]);

  return {
    clearLivePlaybackCountdown,
    clearLiveSpeakingTimeout,
    stopLiveSyntheticAudioSync,
    finishLiveSpeaking,
    shouldSuppressInitialActiveListenPrompt,
    playLivePromptAudio,
    dispatchLiveBlendshapePlayback,
    setPromptPlaybackUnlocked
  };
}

export default useLivePromptPlayback;
